/**
 * Category class for grouping related projects or subcategories
 * Supports recursive hierarchical structure
 */

import { Project } from "./Project";
import { EffortContainerBase } from "./EffortContainerBase";
import { getCategoryLevelColor } from "../config/categoryColors";

/**
 * Category class representing a group of related projects and/or subcategories
 * Extends EffortContainerBase to inherit common aggregation logic
 * Can contain either projects or subcategories (recursive structure)
 */
export class Category extends EffortContainerBase {
  readonly id: number | null;
  readonly name: string;
  readonly color: string;
  private projects: Project[];
  private subcategories: Category[];
  readonly parentId: number | null;
  readonly level: number; // Depth level in hierarchy (0 = root)

  constructor(data: {
    id: number | null;
    name: string;
    color?: string; // Now optional - defaults to level-based color
    projects?: Project[];
    subcategories?: Category[];
    parentId?: number | null;
    level?: number;
  }) {
    super();
    this.id = data.id;
    this.name = data.name;
    this.level = data.level || 0;
    // Use provided color or default to level-based color
    this.color = data.color || getCategoryLevelColor(this.level);
    this.projects = data.projects || [];
    this.subcategories = data.subcategories || [];
    this.parentId = data.parentId || null;
  }

  /**
   * Get all projects in this category and all subcategories recursively
   * @returns Array of Project instances
   */
  getAllProjects(): Project[] {
    const directProjects = [...this.projects];
    const subProjects = this.subcategories.flatMap((sub) =>
      sub.getAllProjects()
    );
    return [...directProjects, ...subProjects];
  }

  /**
   * Get direct projects (not including subcategories)
   * @returns Array of Project instances directly in this category
   */
  getProjects(): Project[] {
    return this.projects;
  }

  /**
   * Get direct subcategories
   * @returns Array of Category instances
   */
  getSubcategories(): Category[] {
    return this.subcategories;
  }

  /**
   * Check if this category has subcategories
   * @returns True if category has subcategories
   */
  hasSubcategories(): boolean {
    return this.subcategories.length > 0;
  }

  /**
   * Check if this category has projects
   * @returns True if category has direct projects
   */
  hasProjects(): boolean {
    return this.projects.length > 0;
  }

  /**
   * Check if this is a leaf category (has projects, no subcategories)
   * @returns True if leaf category
   */
  isLeaf(): boolean {
    return this.projects.length > 0 && this.subcategories.length === 0;
  }

  /**
   * Add a project to this category (leaf operation)
   * @param project - Project instance to add
   */
  addProject(project: Project): void {
    this.projects.push(project);
    this.invalidateCache();
  }

  /**
   * Add a subcategory to this category
   * @param category - Category instance to add
   */
  addSubcategory(category: Category): void {
    this.subcategories.push(category);
    this.invalidateCache();
  }

  /**
   * Remove a project from this category
   * @param projectId - ID of project to remove
   */
  removeProject(projectId: number): void {
    this.projects = this.projects.filter((p) => p.id !== projectId);
    this.invalidateCache();
  }

  /**
   * Remove a subcategory from this category
   * @param categoryId - ID of subcategory to remove
   */
  removeSubcategory(categoryId: number): void {
    this.subcategories = this.subcategories.filter((c) => c.id !== categoryId);
    this.invalidateCache();
  }

  /**
   * Update a project in this category
   * @param project - Updated project instance
   */
  updateProject(project: Project): void {
    const index = this.projects.findIndex((p) => p.id === project.id);
    if (index !== -1) {
      this.projects[index] = project;
      this.invalidateCache();
    }
  }

  /**
   * Find a project by ID (searches recursively)
   * @param projectId - ID of project to find
   * @returns Project instance or undefined
   */
  findProject(projectId: number): Project | undefined {
    // Check direct projects first
    const directProject = this.projects.find((p) => p.id === projectId);
    if (directProject) return directProject;

    // Search in subcategories
    for (const subcat of this.subcategories) {
      const found = subcat.findProject(projectId);
      if (found) return found;
    }

    return undefined;
  }

  /**
   * Find a subcategory by ID (searches recursively)
   * @param categoryId - ID of category to find
   * @returns Category instance or undefined
   */
  findSubcategory(categoryId: number): Category | undefined {
    // Check direct subcategories first
    const directSubcat = this.subcategories.find((c) => c.id === categoryId);
    if (directSubcat) return directSubcat;

    // Search in nested subcategories
    for (const subcat of this.subcategories) {
      const found = subcat.findSubcategory(categoryId);
      if (found) return found;
    }

    return undefined;
  }

  /**
   * Check if category contains a specific project (searches recursively)
   * @param projectId - ID of project to check
   * @returns True if project is in this category or subcategories
   */
  hasProject(projectId: number): boolean {
    return this.findProject(projectId) !== undefined;
  }

  /**
   * Get the total effort across all projects in this category
   * @returns Total effort (FTE)
   */
  getTotalEffort(): number {
    return this.projects.reduce((total, project) => total + project.effort, 0);
  }

  /**
   * Get all categories in the tree (this + all subcategories recursively)
   * @returns Array of all categories in the hierarchy
   */
  getAllCategories(): Category[] {
    const allCategories: Category[] = [this];
    for (const subcat of this.subcategories) {
      allCategories.push(...subcat.getAllCategories());
    }
    return allCategories;
  }

  /**
   * Get the indentation string based on level
   * @returns String of spaces for indentation
   */
  getIndentation(): string {
    return "  ".repeat(this.level);
  }

  /**
   * Create a copy of this category with updated properties
   * @param updates - Properties to update
   * @returns New Category instance with updates applied
   */
  update(
    updates: Partial<{
      id: number | null;
      name: string;
      color: string;
      parentId: number | null;
      level: number;
    }>
  ): Category {
    return new Category({
      id: updates.id !== undefined ? updates.id : this.id,
      name: updates.name !== undefined ? updates.name : this.name,
      color: updates.color !== undefined ? updates.color : this.color,
      projects: this.projects, // Keep same projects
      subcategories: this.subcategories, // Keep same subcategories
      parentId:
        updates.parentId !== undefined ? updates.parentId : this.parentId,
      level: updates.level !== undefined ? updates.level : this.level,
    });
  }
}
